import { api } from '@/lib/http';
import type {
    TaskItem,
    TaskKind,
    TaskStatus,
    TaskScope,
    TaskOutcome,
} from '@/modules/shared/types/yc-demo';

/**
 * Tasks inbox client. Backend routes mounted under /api/v2/tasks (Plan 01);
 * @/lib/http already prefixes /api so callers use /v2/...
 */
export interface CreateTaskPayload {
    applicationId: string;
    kind: TaskKind;
    /** Provide exactly one of assigneeUserId or assigneeEmail. */
    assigneeUserId?: string | null;
    assigneeEmail?: string | null;
    assigneeDisplayName?: string | null;
    scope?: TaskScope;
    priority?: 'low' | 'normal' | 'high';
    dueAt?: string | null;
    previewText?: string | null;
    sendInviteEmail?: boolean;
}

export interface UpdateTaskPayload {
    status?: TaskStatus;
    outcome?: TaskOutcome;
    outcomeNote?: string;
}

export const tasksApi = {
    async list(params?: {
        status?: TaskStatus | 'all';
        kind?: TaskKind;
        priority?: 'high';
        workspaceId?: string;
    }) {
        const { data } = await api.get<{ tasks: TaskItem[] }>('/v2/tasks', { params });
        return data.tasks;
    },

    async listMyWorkspaces() {
        const { data } = await api.get<{ workspaces: Array<{ _id: string; name: string }> }>(
            '/v2/tasks/workspaces',
        );
        return data.workspaces;
    },

    async get(id: string) {
        const { data } = await api.get<{ task: TaskItem }>(`/v2/tasks/${id}`);
        return data.task;
    },

    async update(id: string, body: UpdateTaskPayload) {
        const { data } = await api.patch<{ task: TaskItem }>(`/v2/tasks/${id}`, body);
        return data.task;
    },

    /**
     * Convenience for the common "mark complete" path. Equivalent to
     * `update(id, { status: 'completed' })`.
     */
    async setStatus(id: string, status: TaskStatus) {
        return this.update(id, { status });
    },

    async create(payload: CreateTaskPayload) {
        const { data } = await api.post<{ task: TaskItem }>('/v2/tasks', payload);
        return data.task;
    },

    async sendInvite(id: string) {
        const { data } = await api.post<{ ok: boolean; sentAt: string | null }>(
            `/v2/tasks/${id}/invite`,
        );
        return data;
    },

    async revoke(id: string) {
        const { data } = await api.delete<{ task: TaskItem }>(`/v2/tasks/${id}`);
        return data.task;
    },

    // ── Reviewer auth (no auth required) ─────────────────────────────────

    async getOrgForReview(orgKey: string) {
        const { data } = await api.get<{
            organization: { _id: string; name: string; logoKey: string | null };
        }>(`/v2/tasks/auth/org/${orgKey}`);
        return data.organization;
    },

    async requestOTP(orgKey: string, email: string) {
        const { data } = await api.post<{ ok: boolean }>('/v2/tasks/auth/request-otp', {
            orgKey,
            email,
        });
        return data;
    },

    async verifyOTP(orgKey: string, email: string, otp: string) {
        const { data } = await api.post<{
            accessToken: string;
            refreshToken: string;
            tokenType: string;
            expiresIn: string;
            expiresAt: string;
            organizationId: string;
            email: string;
        }>('/v2/tasks/auth/verify-otp', { orgKey, email, otp });
        return data;
    },

    async refreshInviteToken(refreshToken: string) {
        const { data } = await api.post<{
            accessToken: string;
            refreshToken: string;
            tokenType: string;
            expiresIn: string;
            expiresAt: string;
        }>('/v2/tasks/auth/refresh', { refreshToken });
        return data;
    },

    async logoutInvite() {
        await api.post('/v2/tasks/auth/logout');
    },
};
